"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { getMixpanelClientSync } from "@repo/tracking/mixpanel/lib/mixpanel-client";
import { waitForGaSessionCookie } from "@repo/tracking/gtm/lib/ga-session-id-client";
import { getDiscoveryPageViewEvent } from "@/lib/analytics/getDiscoveryPageViewMetadata";
import { trackDiscoveryMixpanelPageView } from "@/lib/analytics/trackDiscoveryMixpanelPageView";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
const POLL_INTERVAL_MS = 50;

/**
 * Runs `register` once Mixpanel is actually initialized, without ever
 * initializing it itself. `MixpanelInit` (root layout) is the sole owner of
 * initialization — it resolves `isBot` and `eventPrefix` server-side first.
 * Calling `getMixpanelClient()` here instead would risk this component
 * winning that race and locking in `initMixpanelClient()`'s bare defaults
 * (`eventPrefix: ""`, `isBot: false`) for the rest of the session, since
 * whichever caller reaches it first wins — silently dropping the "Discovery"
 * prefix from every other client-tracked event and skipping bot filtering.
 */
function registerOnceMixpanelReady(register: (mixpanel: ReturnType<typeof getMixpanelClientSync>) => void, isCancelled: () => boolean) {
  const mixpanel = getMixpanelClientSync();
  if (mixpanel) {
    register(mixpanel);
    return;
  }
  setTimeout(() => {
    if (!isCancelled()) {
      registerOnceMixpanelReady(register, isCancelled);
    }
  }, POLL_INTERVAL_MS);
}

/**
 * Registers `platform_user_id` as a Mixpanel super property. Page `event_key`
 * is registered in-memory only, then the registrar calls `track_pageview`
 * with the current route's key so `$mp_web_page_view` matches the URL.
 *
 * `platformUserId` is passed from a server component (layout resolves
 * `getSession()`) instead of being re-fetched here. Set once per full page
 * load: login and logout are full navigations, so session state does not
 * change mid-visit without a reload.
 */
export function DiscoveryPageViewMetadataRegistrar(t0) {
  const $ = _c(9);
  const {
    platformUserId
  } = t0;
  const pathname = usePathname();
  const lastFiredPathnameRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== platformUserId) {
    t1 = () => {
      let cancelled = false;
      registerOnceMixpanelReady(mixpanel => mixpanel?.register({
        platform_user_id: platformUserId
      }), () => cancelled);
      return () => {
        cancelled = true;
      };
    };
    t2 = [platformUserId];
    $[0] = platformUserId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== pathname) {
    t3 = () => {
      let cancelled_0 = false;
      registerOnceMixpanelReady(mixpanel_0 => trackDiscoveryMixpanelPageView(mixpanel_0, pathname), () => cancelled_0);
      return () => {
        cancelled_0 = true;
      };
    };
    t4 = [pathname];
    $[3] = pathname;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== pathname) {
    t5 = () => {
      if (lastFiredPathnameRef.current === pathname) {
        return;
      }
      const pageViewEvent = getDiscoveryPageViewEvent(pathname);
      if (!pageViewEvent) {
        lastFiredPathnameRef.current = pathname;
        return;
      }
      let cancelled_1 = false;
      waitForGaSessionCookie(process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID).then(() => {
        if (cancelled_1) {
          return;
        }
        lastFiredPathnameRef.current = pathname;
        sendDiscoveryEventBeacon(pageViewEvent.eventKey, pageViewEvent.properties, {
          referrer: document.referrer || null
        });
      });
      return () => {
        cancelled_1 = true;
      };
    };
    t6 = [pathname];
    $[6] = pathname;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  return null;
}